import React, { useState, useEffect } from 'react';
import { X, Send, Loader2, CheckCircle, LogOut, Trash2 } from 'lucide-react';
import { ApiService } from '../../services/ApiService.ts';
import { useTranslation } from '../../i18n.ts';
import { useToast } from '../shared/Toast.tsx';
import { useConfirm } from '../shared/ModalDialogs.tsx';
import { Modal } from '../ui/Modal.tsx';
import { useTierAccess } from '../../hooks/useTierAccess.ts';
import { getErrorMessage } from '../../services/api/base.ts';

interface InviteMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  /**
   * #630/#631: rol van de huidige gebruiker in dit project. 'owner' ziet de
   * beheer-UI, anderen zien "Project verlaten".
   *
   * #1275 (NC #648): `null` is expliciet toegestaan omdat de server dat kan
   * teruggeven. Ontbreekt de rol, dan behandelen we die als NIET-owner — een
   * onbekende rol mag nooit beheerrechten opleveren.
   */
  currentUserRole?: 'owner' | 'editor' | 'viewer' | null;
  /** Callback als de gebruiker het project verlaat — caller moet naar projectenlijst navigeren */
  onLeft?: () => void;
}

function relativeTime(
  dateStr: string,
  t: (key: string, params?: Record<string, string>) => string
): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return t('ogMinutesAgoShort', { n: String(mins) });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}${t('hoursAgo')}`;
  const days = Math.floor(hours / 24);
  return `${days}${t('daysAgo')}`;
}

const InviteMemberModal: React.FC<InviteMemberModalProps> = ({
  isOpen,
  onClose,
  projectId,
  currentUserRole,
  onLeft,
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const confirm = useConfirm();
  // #1730 — alleen de eigenaar nodigt uit, dus zijn tier is de tier die telt.
  // Gratis heeft wel meekijkers maar geen bewerkers.
  const { getLimit } = useTierAccess();
  const editorsAllowed = getLimit('maxEditorsPerProject') !== 0;
  const defaultRole = editorsAllowed ? 'editor' : 'viewer';
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'editor' | 'viewer'>(defaultRole);
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviteSending, setInviteSending] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState(false);
  const [pendingInvitations, setPendingInvitations] = useState<any[]>([]);
  // #630: geaccepteerde members + beheer acties
  const [members, setMembers] = useState<any[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const isOwner = currentUserRole === 'owner';

  useEffect(() => {
    if (isOpen && projectId) {
      setInviteEmail('');
      setInviteRole(defaultRole);
      setInviteMessage('');
      setInviteSuccess(false);
      ApiService.getProjectInvitations(projectId)
        .then((invites: any[]) =>
          setPendingInvitations(invites.filter((i: any) => i.status === 'pending'))
        )
        .catch(() => setPendingInvitations([]));
      // #630: geaccepteerde members laden
      setMembersLoading(true);
      ApiService.getProjectMembers(projectId)
        .then((list: any[]) => {
          // Filter out owner zelf — die is niet relevant voor beheer
          setMembers(list.filter((m: any) => m.role !== 'owner'));
        })
        .catch(() => setMembers([]))
        .finally(() => setMembersLoading(false));
    }
  }, [isOpen, projectId, defaultRole]);

  // #630: rol wijzigen voor een member
  const handleChangeRole = async (memberId: string, newRole: string) => {
    try {
      await ApiService.updateMemberRole(projectId, memberId, newRole);
      setMembers((prev) =>
        prev.map((m) =>
          m.id === memberId || m.account_id === memberId ? { ...m, role: newRole } : m
        )
      );
      toast(t('roleUpdated') || 'Rol bijgewerkt', 'success');
    } catch (err: unknown) {
      toast(getErrorMessage(err, t) || t('operationFailed') || 'Actie mislukt', 'error');
    }
  };

  // #630: member verwijderen
  const handleRemoveMember = async (memberId: string, email: string) => {
    const ok = await confirm({
      title: t('removeMemberConfirmTitle') || 'Lid verwijderen?',
      message:
        t('removeMemberConfirmMessage')?.replace('{email}', email) ||
        `Weet je zeker dat je ${email} wilt verwijderen uit dit project? Ze verliezen direct hun toegang.`,
      confirmLabel: t('remove') || 'Verwijderen',
      cancelLabel: t('cancel') || 'Annuleren',
      variant: 'destructive',
    });
    if (!ok) return;
    try {
      await ApiService.removeMember(projectId, memberId);
      setMembers((prev) => prev.filter((m) => m.id !== memberId && m.account_id !== memberId));
      toast(t('memberRemoved') || 'Lid verwijderd', 'success');
    } catch {
      toast(t('operationFailed') || 'Actie mislukt', 'error');
    }
  };

  // #631: project verlaten (voor niet-owners)
  const handleLeaveProject = async () => {
    const ok = await confirm({
      title: t('leaveProjectConfirmTitle') || 'Project verlaten?',
      message:
        t('leaveProjectConfirmMessage') ||
        'Weet je zeker dat je dit project wilt verlaten? Je kunt later opnieuw uitgenodigd worden.',
      confirmLabel: t('leaveProject') || 'Verlaten',
      cancelLabel: t('cancel') || 'Annuleren',
      variant: 'destructive',
    });
    if (!ok) return;
    try {
      await ApiService.leaveProject(projectId);
      toast(t('projectLeft') || 'Project verlaten', 'success');
      onClose();
      onLeft?.();
    } catch {
      toast(t('operationFailed') || 'Actie mislukt', 'error');
    }
  };

  const handleSendInvite = async () => {
    if (!projectId || !inviteEmail.trim()) return;
    setInviteSending(true);
    try {
      await ApiService.inviteMember(
        projectId,
        inviteEmail.trim(),
        inviteRole,
        inviteMessage.trim() || undefined
      );
      setInviteSuccess(true);
      setInviteEmail('');
      const invites = await ApiService.getProjectInvitations(projectId);
      setPendingInvitations(invites.filter((i: any) => i.status === 'pending'));
    } catch (err: unknown) {
      toast(getErrorMessage(err, t) || t('operationFailed') || 'Versturen mislukt', 'error');
    } finally {
      setInviteSending(false);
    }
  };

  const handleRevokeInvite = async (inviteId: string) => {
    try {
      await ApiService.revokeInvitation(inviteId);
      setPendingInvitations((prev) => prev.filter((i) => i.id !== inviteId));
      toast(t('invitationRevoked') || 'Uitnodiging ingetrokken', 'success');
    } catch {
      toast(t('operationFailed') || 'Actie mislukt', 'error');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} titleId="modal-title-team-invite" size="sm">
      <div>
        <div className="flex items-center justify-between border-b border-pt-forest/10 dark:border-white/10 p-5 pb-4">
          <h3
            id="modal-title-team-invite"
            className="text-[15px] font-bold text-pt-forest dark:text-[var(--text-main)]"
          >
            {isOwner ? t('manageTeam') || 'Team beheren' : t('projectAccess') || 'Project toegang'}
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-pt-forest/5 dark:bg-white/10 flex items-center justify-center text-pt-sage-text dark:text-[var(--text-muted)] hover:text-pt-forest dark:hover:text-[var(--text-main)] transition-colors"
          >
            <X size={16} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          {/* Uitnodig-form alleen voor owners */}
          {isOwner && (
            <>
              <div className="ti-field">
                <label className="ti-label" htmlFor="inviteEmail">
                  {t('emailAddress') || 'E-mailadres'}
                </label>
                <input
                  className="ti-input"
                  type="email"
                  id="inviteEmail"
                  placeholder={t('inviteFormEmailPlaceholder')}
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSendInvite();
                  }}
                  required
                />
              </div>
              <div className="ti-field">
                <label className="ti-label" htmlFor="inviteRole">
                  {t('role') || 'Rol'}
                </label>
                <select
                  className="ti-select"
                  id="inviteRole"
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as 'editor' | 'viewer')}
                >
                  <option value="editor" disabled={!editorsAllowed}>
                    {editorsAllowed
                      ? t('ogRoleEditor')
                      : `${t('ogRoleEditor')} (${t('tierName_pro')})`}
                  </option>
                  <option value="viewer">{t('viewerBadge')}</option>
                </select>
              </div>
              <div className="ti-field">
                <label className="ti-label" htmlFor="inviteMessage">
                  {t('personalMessage') || 'Persoonlijk bericht'}{' '}
                  <span className="ti-optional">({t('optional') || 'optioneel'})</span>
                </label>
                <textarea
                  className="ti-textarea"
                  id="inviteMessage"
                  rows={3}
                  placeholder={t('addMessage') || 'Voeg een bericht toe...'}
                  value={inviteMessage}
                  onChange={(e) => setInviteMessage(e.target.value)}
                />
              </div>
              <button
                className="ti-submit-btn"
                onClick={handleSendInvite}
                disabled={inviteSending || !inviteEmail.trim()}
              >
                {inviteSending ? (
                  <>
                    <Loader2 size={14} className="animate-spin" /> {t('sending') || 'Versturen...'}
                  </>
                ) : (
                  <>
                    <Send size={14} /> {t('sendInvitation') || 'Uitnodiging versturen'}
                  </>
                )}
              </button>
              {inviteSuccess && (
                <div className="ti-success">
                  <CheckCircle size={14} /> {t('invitationSent') || 'Uitnodiging verstuurd!'}
                </div>
              )}
            </>
          )}

          {/* #630: Geaccepteerde members — alleen voor owner */}
          {isOwner && (
            <div className="ti-pending-section">
              <h4 className="ti-pending-title">
                {t('teamMembers') || 'Teamleden'}
                {members.length > 0 && <span style={{ opacity: 0.6 }}> ({members.length})</span>}
              </h4>
              {membersLoading ? (
                <div className="ti-pending-meta" style={{ padding: '8px 0' }}>
                  <Loader2 size={12} className="animate-spin inline" /> {t('loading') || 'Laden...'}
                </div>
              ) : members.length === 0 ? (
                <div className="ti-pending-meta" style={{ padding: '8px 0' }}>
                  {t('noTeamMembersYet') || 'Nog geen teamleden'}
                </div>
              ) : (
                <div className="ti-pending-list">
                  {members.map((m: any) => {
                    const memberId = m.id || m.account_id;
                    const displayName = m.email || m.name || m.account_email || memberId;
                    return (
                      <div className="ti-pending-item" key={memberId}>
                        <div className="ti-pending-info">
                          <span className="ti-pending-email">{displayName}</span>
                          <span className="ti-pending-meta">
                            {t('accepted') || 'Geaccepteerd'}
                            {m.accepted_at && ` · ${relativeTime(m.accepted_at, t)}`}
                          </span>
                        </div>
                        <select
                          className="ti-select"
                          style={{ width: 'auto', minWidth: 80, marginRight: 6 }}
                          value={m.role || 'editor'}
                          onChange={(e) => handleChangeRole(memberId, e.target.value)}
                          title={t('changeRole') || 'Rol wijzigen'}
                        >
                          <option value="editor">{t('ogRoleEditor')}</option>
                          <option value="viewer">{t('viewerBadge')}</option>
                        </select>
                        <button
                          className="ti-revoke-btn"
                          onClick={() => handleRemoveMember(memberId, displayName)}
                          title={t('removeMember') || 'Lid verwijderen'}
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {pendingInvitations.length > 0 && isOwner && (
            <div className="ti-pending-section">
              <h4 className="ti-pending-title">
                {t('pendingInvitations') || 'Openstaande uitnodigingen'}
              </h4>
              <div className="ti-pending-list">
                {pendingInvitations.map((inv: any) => (
                  <div className="ti-pending-item" key={inv.id}>
                    <div className="ti-pending-info">
                      <span className="ti-pending-email">{inv.invitee_email || inv.email}</span>
                      <span className="ti-pending-meta">
                        {inv.role || 'editor'} · {t('sent') || 'Verstuurd'}{' '}
                        {relativeTime(inv.created_at, t)}
                      </span>
                    </div>
                    <button className="ti-revoke-btn" onClick={() => handleRevokeInvite(inv.id)}>
                      {t('revoke') || 'Intrekken'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* #631: "Project verlaten" knop voor niet-owners */}
          {!isOwner && (
            <div
              style={{
                padding: '12px',
                background: 'rgba(155, 17, 30, 0.04)',
                borderRadius: 8,
                border: '1px solid rgba(155, 17, 30, 0.15)',
              }}
            >
              <p
                style={{
                  fontSize: 12,
                  color: 'var(--text-muted)',
                  marginBottom: 10,
                  lineHeight: 1.5,
                }}
              >
                {t('leaveProjectDescription') ||
                  'Je bent deelnemer in dit project. Klik hieronder om je eigen toegang in te trekken.'}
              </p>
              <button
                onClick={handleLeaveProject}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '8px 14px',
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#9b111e',
                  background: 'transparent',
                  border: '1px solid rgba(155, 17, 30, 0.3)',
                  borderRadius: 6,
                  cursor: 'pointer',
                }}
              >
                <LogOut size={14} /> {t('leaveProject') || 'Project verlaten'}
              </button>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default InviteMemberModal;
